import React, { type ReactNode } from "react"

import { DATE_PRESETS } from "@/constants/date-presets"
import {
  Bot,
  Braces,
  Calendar,
  ChartNoAxesCombined,
  ClipboardCheck,
  Tags,
} from "lucide-react"

import { DateRangePanel } from "@/components/date-range/DateRangePanel"
import { FilterPicker } from "@/components/filters/FilterPicker"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import { useConversationsFilter } from "../../contexts/ConversationsFilterContext"
import { useConversationsPopover } from "../../contexts/ConversationsPopoverContext"
import { AgentFilterSection } from "./AgentFilterSection"
import { MetadataFilterSection } from "./MetadataFilterSection"
import { MetricsFilterSection } from "./MetricsFilterSection"
import { ProjectsFilterSection } from "./ProjectsFilterSection"
import { TagsFilterSection } from "./TagsFilterSection"

export function ConversationsFilterDropdown({
  filterEnabled,
  conversations,
  trigger,
}: {
  conversations: ConversationsTableResult[]
  filterEnabled?: boolean
  /** Opens the menu in place of the toolbar's funnel; see `FilterPicker`. */
  trigger?: ReactNode
}) {
  const {
    state: {
      dateRange,
      agentIds,
      metadataFilters,
      metricIds,
      metricValueFilters,
      tagFilters,
      projectIds,
    },
    handlerSetDateRange,
    handlerClearDateRange,
  } = useConversationsFilter()
  const { isOpen, onOpenChange, filterSection, setFilterSection } =
    useConversationsPopover("filters")
  const panel = { isOpen: true, onToggle: () => {}, filterEnabled }
  // The toolbar's funnel shares its open menu and its open section with the
  // table's other popovers. An entry point with its own trigger — the `+`
  // beside the applied chips — leaves both uncontrolled instead, so the menu
  // opens against the button that was pressed and nothing about it outlives
  // that button when the last filter takes the chip bar away.
  const menuProps = trigger
    ? {}
    : {
        open: isOpen,
        onOpenChange,
        section: filterSection,
        onSectionChange: setFilterSection,
      }
  return (
    <FilterPicker
      label="Filter conversations"
      {...menuProps}
      trigger={trigger}
      activeCount={
        Number(Boolean(dateRange.start || dateRange.end)) +
        agentIds.length +
        metadataFilters.length +
        metricIds.length +
        metricValueFilters.length +
        tagFilters.length +
        projectIds.length
      }
      items={[
        {
          id: "date",
          label: "Conversation Time",
          icon: <Calendar />,
          activeCount: Number(Boolean(dateRange.start || dateRange.end)),
          content: (
            <DateRangePanel
              value={dateRange}
              onRangeChange={({ start, end }) =>
                handlerSetDateRange(start, end)
              }
              presets={DATE_PRESETS}
              onClear={handlerClearDateRange}
            />
          ),
        },
        {
          id: "agents",
          label: "Agent",
          icon: <Bot />,
          activeCount: agentIds.length,
          content: <AgentFilterSection {...panel} />,
        },
        {
          id: "metrics",
          label: "Metric",
          icon: <ChartNoAxesCombined />,
          activeCount: metricIds.length + metricValueFilters.length,
          content: (
            <MetricsFilterSection {...panel} conversations={conversations} />
          ),
        },
        {
          id: "tags",
          label: "Tag",
          icon: <Tags />,
          activeCount: tagFilters.length,
          content: <TagsFilterSection {...panel} />,
        },
        {
          id: "projects",
          label: "Project",
          icon: <ClipboardCheck />,
          activeCount: projectIds.length,
          content: <ProjectsFilterSection {...panel} />,
        },
        {
          id: "metadata",
          label: "Metadata",
          icon: <Braces />,
          activeCount: metadataFilters.length,
          content: <MetadataFilterSection {...panel} />,
        },
      ]}
    />
  )
}
